import {Component, NgZone, Input, Output, EventEmitter, OnInit, ChangeDetectorRef} from "@angular/core";

import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {ComponentBase} from "../base/ComponentBase";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {PipelineService, IGlossaryInfo} from "../services/pipeline/pipeline.service";
import {UtilitiesService} from "../services/utilities.service";

import {
    ISelection, IGlossaryEntry, ISyllables, IDefinition, IPartOfSpeech,
    IFact, IContext, ITranslation, ICredit, IAnswer
} from "../api/data-model";
import {CssWord} from "../content/content-view.objects";
import {RaVersion} from "../services/configuration/ra.version";
import {ClientConfiguration} from "../services/configuration/client.config";
import {HttpClient} from "@angular/common/http";
import {AudioState, Scilearn} from "../constants";

@Component({
    selector: "glossary-editor-detail",
    templateUrl: "./glossary.editor.detail.component.html",
    styleUrls: ["./pipeline.component.css"]
})
export class GlossaryEditorDetailComponent extends ComponentBase implements OnInit {

    @Output()
    public saveWithLinkRequest:EventEmitter<IGlossaryInfo> = new EventEmitter<IGlossaryInfo>();

    @Output()
    public updateRequest:EventEmitter<IGlossaryInfo> = new EventEmitter<IGlossaryInfo>();

    @Output()
    public deleteRequest:EventEmitter<IGlossaryInfo> = new EventEmitter<IGlossaryInfo>();

    @Output()
    public cancelRequest:EventEmitter<IGlossaryInfo> = new EventEmitter<IGlossaryInfo>();

    @Output()
    public makeDefinitionRequest:EventEmitter<IGlossaryInfo> = new EventEmitter<IGlossaryInfo>();

    @Input()
    public idx:string;

    @Input()
    public glossaryInfo: IGlossaryInfo = null;

    @Input()
    public saveInProgress = false;

    @Input()
    public selectedWord: CssWord = null;

    public newTranslation: ITranslation = {word: "", id: "", audio: "", language: "es"};
    public imageCredit: ICredit = {attribution: "", description: "", location: 0.0, type: "IMAGE"};
    private originalGlossaryInfo: IGlossaryInfo;

    constructor(
        private http: HttpClient,
        private eventBus: EventBus,
        private modalConfig: ModalConfig,
        private logService: BugsnagLoggingService,
        private pipelineService: PipelineService,
        private config: ClientConfiguration,
        private ref: ChangeDetectorRef
        ) {
        super("GlossaryEditorDetailComponent");
    }

    ngOnInit(): void {
        this.init();
    }

    private init() {
        this.originalGlossaryInfo = JSON.parse(JSON.stringify(this.glossaryInfo));
        if (this.glossaryInfo.entry.imageCredits && this.glossaryInfo.entry.imageCredits.length > 0) {
            this.imageCredit = JSON.parse(JSON.stringify(this.glossaryInfo.entry.imageCredits[0]));
        }
    }

    public changeDisabled(): boolean {
        return this.saveInProgress;
    }

    public dataMissing(): boolean {
        return !this.newTranslation.word || !this.glossaryInfo.entry.syllables.text
            || !this.glossaryInfo.entry.definition.text || !this.selectedWord;
    }

    public isV1Entry():boolean {
        return (this.glossaryInfo && this.glossaryInfo.entry.id && !!this.glossaryInfo.entry.id.match(/^[0-9]+$/));
    }

    public isNewEntry():boolean {
        return !this.glossaryInfo || !this.glossaryInfo.entry.id;
    }

    public showAddTranslation():boolean {
        return this.glossaryInfo && this.glossaryInfo.entry.translations.length === 0;
    }

    public showAddCredit():boolean {
        return !!this.glossaryInfo.entry.context.media;
    }

    public save(doLink: boolean): void {
        let doSave = true;
        if (this.flagAudioTextMismatch()) {
            if (confirm("This entry has audio scripted or recorded which now may not match edited text (it will be marked as needing re-recording). Are you sure?")
                !== true) {
                doSave = false;
            }
        }
        if (doSave) {
            this.saveGlossaryEntry(doLink);
            this.init();
        } else { // not saving, so revert to original entry
            this.glossaryInfo = JSON.parse(JSON.stringify(this.originalGlossaryInfo));
            if (this.originalGlossaryInfo.entry.imageCredits && this.originalGlossaryInfo.entry.imageCredits.length > 0) {
                this.imageCredit = JSON.parse(JSON.stringify(this.originalGlossaryInfo.entry.imageCredits[0]));
            }
            this.ref.detectChanges();
        }
    }

    public saveGlossaryEntry(doLink: boolean):void {
        // TODO: only works for the single translation case
        if (this.newTranslation.word) {
            this.addSingleTranslation();
        } else {
            if ((this.glossaryInfo.entry.translations.length > 0) &&
                (!this.glossaryInfo.entry.translations[0].id ||
                 !this.glossaryInfo.entry.translations[0].id.match(/^[0-9]+$/)) ) {
                this.logService.log("getting new translation id . . . ");
            }
        }
        if (!!this.imageCredit.attribution) {
            this.glossaryInfo.entry.imageCredits = [JSON.parse(JSON.stringify(this.imageCredit))];
            this.imageCredit = {attribution: "", description: "", location: 0.0, type: "IMAGE"};
        }
        if (doLink) {
            this.glossaryInfo.isDefinition = !this.glossaryInfo.isDefinition;
        }
        if (doLink && this.glossaryInfo.isDefinition) {
            this.saveWithLinkRequest.emit(this.glossaryInfo);
        } else {
            this.updateRequest.emit(this.glossaryInfo);
        }
    }

    public deleteGlossaryEntry():void {
        if (this.isNewEntry()) {
            this.cancelRequest.emit(this.glossaryInfo);
        } else {
            this.deleteRequest.emit(this.glossaryInfo);
        }
    }

    public addAsDefinition():void {
        /*if (this.isTheDefinition()) {
            this.selectedWord.glossary = "";
        } else {
            this.selectedWord.glossary = this.glossaryEntry.id;
        }*/
        this.glossaryInfo.isDefinition = !this.glossaryInfo.isDefinition;
        this.makeDefinitionRequest.emit(this.glossaryInfo);
    }

    public addSingleTranslation(): void {
        this.logService.log("adding single translation for translation:");
        if (this.newTranslation.word) {
            this.glossaryInfo.entry.translations = [JSON.parse(JSON.stringify(this.newTranslation))];
        }
        this.newTranslation = {word: "", id: "", audio: "", language: "es"};
    }

    private flagAudioTextMismatch(): boolean {
        if (this.isNewEntry()) {
            return false;
        }
        let flagMismatch = false;
        const entry = this.glossaryInfo.entry;
        const originalEntry = this.originalGlossaryInfo.entry;
        if (entry.definition.audio && (entry.definition.text !== originalEntry.definition.text)) {
            flagMismatch = true;
            if (entry.definition.audioState !== AudioState.UNRECORDED) {
                entry.definition.audioState = AudioState.RERECORD;
            }
        }
        if (entry.context.audio && (entry.context.text !== originalEntry.context.text)) {
            flagMismatch = true;
            if (entry.context.audioState !== AudioState.UNRECORDED) {
                entry.context.audioState = AudioState.RERECORD;
            }
        }
        if (entry.fact.audio && (entry.fact.text !== originalEntry.fact.text)) {
            flagMismatch = true;
            if (entry.fact.audioState !== AudioState.UNRECORDED) {
                entry.fact.audioState = AudioState.RERECORD;
            }
        }
        if (entry.translations?.length > 0 && entry.translations[0].audio &&
            (!originalEntry.translations || originalEntry.translations.length === 0
            || entry.translations[0].word !== originalEntry.translations[0].word)) {
            flagMismatch = true;
            if (entry.translations[0].audioState !== AudioState.UNRECORDED) {
                entry.translations[0].audioState = AudioState.RERECORD;
            }
        }
        return flagMismatch;
    }

    // translation id just has to be unique within a glossary entry
    private getTranslationId(newTranslation: ITranslation, existingTranslations:ITranslation[]): string {
        let id = UtilitiesService.convertWordOrPhraseToID(newTranslation.word);
        let newIndex = existingTranslations
            .filter((t) => t.id.startsWith(id) && t.language === newTranslation.language).length + 1;
        id += newIndex.toString(10);
        this.logService.log("new translation id:" + id);
        return id;
    }

    public deleteTranslation(translation:ITranslation):void {
        this.logService.log("deleting translation");
        let index = this.glossaryInfo.entry.translations.indexOf(translation, 0);
        if (index > -1) {
            this.glossaryInfo.entry.translations.splice(index, 1);
        }
    }


    public getImageSrc(img: string): string {
        return img ? this.config.selectionImageUrl(img) : "";
    }

    public isImageThere(image): boolean {
        return image !== "";
    }

}
